import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Trash2,
  Info,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { useNotificationStore, type Notification } from '@stores/notificationStore';
import { apiClient } from '../services/api-client';
import { formatDistanceToNow } from 'date-fns';

/**
 * formatDistanceToNow throws RangeError: Invalid time value on an Invalid
 * Date. notification.createdAt is passed straight through from the raw
 * websocket payload with no runtime validation and is also persisted to
 * localStorage, so a malformed entry (a backend event type that forgot to
 * send createdAt, or a stale record from an older bundle) can crash this
 * every time the bell dropdown opens (Bug #9408).
 */
export function formatRelativeTimestamp(createdAt: string | undefined | null): string {
  const date = new Date(createdAt ?? '');
  if (Number.isNaN(date.getTime())) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
}

const notificationIcons: Record<string, typeof Info> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

type Tone = { color: string; background: string; borderColor: string };

const BRASS: Tone = {
  color: 'var(--co-brass-ink)',
  background: 'var(--co-brass-wash)',
  borderColor: 'var(--co-brass-line)',
};
const GREEN: Tone = {
  color: 'var(--co-green)',
  background: 'var(--co-green-wash)',
  borderColor: 'var(--co-green-line)',
};
const TERRACOTTA: Tone = {
  color: 'var(--co-terracotta)',
  background: 'var(--co-terracotta-wash)',
  borderColor: 'var(--co-terracotta-line)',
};
const BURGUNDY: Tone = {
  color: 'var(--co-burgundy)',
  background: 'var(--co-burgundy-wash)',
  borderColor: 'var(--co-burgundy-line)',
};
const NEUTRAL: Tone = {
  color: 'var(--co-ink-3)',
  background: 'var(--co-selected)',
  borderColor: 'var(--co-line-strong)',
};

const notificationColors: Record<string, Tone> = {
  info: BRASS,
  success: GREEN,
  warning: TERRACOTTA,
  error: BURGUNDY,
};

// Get icon with fallback to Info
function getNotificationIcon(type: string) {
  return notificationIcons[type] || Info;
}

// Get color tone with fallback to info style
function getNotificationColors(type: string) {
  return notificationColors[type] || notificationColors.info;
}

// Severity badge styles
const severityStyles: Record<string, { tone: Tone; label: string }> = {
  minor: { tone: TERRACOTTA, label: 'Minor' },
  major: { tone: TERRACOTTA, label: 'Major' },
  critical: { tone: BURGUNDY, label: 'Critical' },
};

// Role badge styles
const roleStyles: Record<string, { tone: Tone; label: string }> = {
  assignee: { tone: BRASS, label: 'Assigned' },
  admin: { tone: BRASS, label: 'Admin' },
  team_lead: { tone: BRASS, label: 'Team Lead' },
};

const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-[5px] border px-[6px] py-[2px] ' +
  'font-mono text-[9px] font-semibold uppercase tracking-[.1em]';

function NotificationItem({
  notification,
  onMarkAsRead,
  onRemove,
  onClick,
}: {
  notification: Notification;
  onMarkAsRead: () => void;
  onRemove: () => void;
  onClick: () => void;
}) {
  const Icon = getNotificationIcon(notification.type);
  const tone = getNotificationColors(notification.type);
  const severity = notification.metadata?.severity;
  const recipientRole = notification.metadata?.recipientRole;
  const ticketNumber = notification.metadata?.ticketNumber;
  const unread = !notification.read;

  // Unread rows carry a coloured edge — burgundy for critical, brass otherwise.
  // The row itself stays on the raised surface so a list of breaches is not a
  // wall of pink.
  const edge = !unread
    ? 'transparent'
    : severity === 'critical'
      ? 'var(--co-burgundy)'
      : 'var(--co-brass)';

  return (
    <div
      className="cursor-pointer border-b border-l-[3px] border-solid px-4 py-3 transition-[filter] duration-[120ms] last:border-b-0 hover:brightness-[.97]"
      style={{
        borderLeftColor: edge,
        borderBottomColor: 'var(--co-line-subtle)',
        background: unread ? 'var(--co-raised)' : 'var(--co-page)',
      }}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border"
          style={tone}
        >
          <Icon className="h-4 w-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p
              className={`min-w-0 truncate text-[13px] leading-[1.3] tracking-[-.01em] ${
                unread ? 'font-bold text-[var(--co-ink)]' : 'font-medium text-[var(--co-ink-2)]'
              }`}
            >
              {notification.title}
            </p>
            {unread && (
              <span
                className="h-[6px] w-[6px] flex-none rounded-full bg-[var(--co-brass)]"
                aria-hidden="true"
              />
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
            {notification.message}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-[6px]">
            {/* Severity badge for SLA breaches */}
            {severity && severityStyles[severity] && (
              <span className={CHIP} style={severityStyles[severity].tone}>
                {severityStyles[severity].label}
              </span>
            )}
            {/* Ticket number badge */}
            {ticketNumber && (
              <span className={CHIP} style={NEUTRAL}>
                #{ticketNumber}
              </span>
            )}
            {/* Role indicator for admins/team leads */}
            {recipientRole && recipientRole !== 'assignee' && roleStyles[recipientRole] && (
              <span className={CHIP} style={roleStyles[recipientRole].tone}>
                {roleStyles[recipientRole].label}
              </span>
            )}
            <span className="font-mono text-[10px] tracking-[.04em] text-[var(--co-ink-4)]">
              {formatRelativeTimestamp(notification.createdAt)}
            </span>
            {notification.link && (
              <ExternalLink className="h-3 w-3 text-[var(--co-ink-4)]" />
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-[2px]">
          {unread && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead();
              }}
              className="rounded-md p-1 text-[var(--co-ink-4)] transition-colors duration-[120ms] hover:bg-[var(--co-brass-wash)] hover:text-[var(--co-brass-ink)]"
              title="Mark as read"
            >
              <Check className="h-[15px] w-[15px]" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="rounded-md p-1 text-[var(--co-ink-4)] transition-colors duration-[120ms] hover:bg-[var(--co-burgundy-wash)] hover:text-[var(--co-burgundy)]"
            title="Remove"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function NotificationCenter() {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    notifications,
    unreadCount,
    isOpen,
    toggleOpen,
    setIsOpen,
    markAsRead,
    markAllAsRead,
    removeNotification,
    addNotification,
    clearAll,
  } = useNotificationStore();

  // Background poll: fetch unread count + notifications every 30s (even when dropdown is closed)
  // This ensures the bell badge shows up without needing WebSocket
  const fetchNotificationsFromApi = useCallback(async () => {
    try {
      const res = await apiClient.get('/notifications');
      const data = res.data;
      const apiNotifications = data?.notifications ?? (Array.isArray(data) ? data : []);
      const store = useNotificationStore.getState();
      const existingIds = new Set(store.notifications.map((n) => n.id));
      const dismissed = new Set(store.dismissedIds);

      const typeMap: Record<string, Notification['type']> = {
        ticket_assigned: 'info',
        ticket_reopened: 'warning',
        new_message: 'info',
        sla_breach: 'error',
        sla_warning: 'warning',
        sla_escalation: 'warning',
        // Someone is waiting on an approver's decision.
        ticket_access_requested: 'warning',
        ticket_access_decided: 'info',
      };

      for (const n of apiNotifications) {
        if (existingIds.has(n.id) || dismissed.has(n.id)) continue;

        // Strip severity prefix like [Critical], [Major], [Minor] from title
        let title = n.title || n.subject || '';
        title = title.replace(/^\[(Critical|Major|Minor|Warning)\]\s*/i, '');

        // Resolve the deep link that opens the notification's ticket on click.
        // Prefer an explicit `data.link`; otherwise build the ticket route from a
        // ticket reference. `entityId` is the column that is always populated on
        // ticket notifications (the list endpoint now returns it) — gate it on
        // the ticket entity type so non-ticket notifications never build a bogus
        // `/tickets/` link. `data.ticketId`/`data.entityId` remain as fallbacks
        // for older payloads.
        const isTicket = n.entityType === 'ticket' || n.data?.entityType === 'ticket';
        const link =
          n.data?.link ||
          (isTicket && n.entityId
            ? `/tickets/${n.entityId}`
            : n.data?.ticketId
              ? `/tickets/${n.data.ticketId}`
              : n.data?.entityId
                ? `/tickets/${n.data.entityId}`
                : undefined);

        addNotification({
          id: n.id,
          type: typeMap[n.type] || 'info',
          title,
          message: n.message || n.body || '',
          link,
          createdAt: n.createdAt,
          read: n.read ?? false,
          metadata: n.data,
        });
      }
    } catch {
      // Silent — will retry on next interval
    }
  }, [addNotification]);

  useEffect(() => {
    fetchNotificationsFromApi(); // immediate first call
    const interval = setInterval(fetchNotificationsFromApi, 30000);
    return () => clearInterval(interval);
  }, [fetchNotificationsFromApi]);

  // Also refresh immediately when dropdown opens
  useEffect(() => {
    if (isOpen) fetchNotificationsFromApi();
  }, [isOpen, fetchNotificationsFromApi]);

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, setIsOpen]);

  // Handle keyboard escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, setIsOpen]);

  const handleNotificationClick = (notification: Notification) => {
    // Opening a notification dismisses it from the list (and the backend) so it
    // doesn't linger after the user has acted on it. removeNotification also
    // records the id in dismissedIds, so the 30s background poll won't re-add it.
    removeNotification(notification.id);
    apiClient.delete(`/notifications/${notification.id}`).catch(() => {});

    // Resolve the ticket route from any reference the notification carries.
    // Newer notifications set `link`; others only carry the ticket id in
    // metadata (e.g. SLA breaches use metadata.ticketId), so fall back to that
    // so every ticket notification opens on click.
    const target =
      notification.link ||
      (notification.metadata?.ticketId
        ? `/tickets/${notification.metadata.ticketId}`
        : undefined);

    if (target) {
      navigate(target);
      setIsOpen(false);
    }
  };

  const handleRemoveNotification = (id: string) => {
    removeNotification(id);
    // Persist deletion to backend (fire-and-forget)
    apiClient.delete(`/notifications/${id}`).catch(() => {});
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    apiClient.post('/notifications/mark-all-read').catch(() => {});
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger Button */}
      <button
        onClick={toggleOpen}
        className="relative flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] text-[var(--co-ink-2)] transition-colors duration-[120ms] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]"
        aria-label={
          unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'
        }
      >
        <Bell className="h-3.5 w-3.5" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-1.5 -top-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full px-[4px] font-mono text-[9px] font-semibold ring-2 ring-[var(--co-raised)]"
            style={{ background: 'var(--co-burgundy)', color: 'var(--co-ink-invert)' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div
          className="absolute right-0 z-50 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)]"
          style={{ borderRadius: 12, boxShadow: 'var(--co-shadow-pop)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-[var(--co-line-subtle)] bg-[var(--co-raised)] px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <h3 className="text-[15px] font-bold tracking-[-.018em] text-[var(--co-ink)]">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <span className={CHIP} style={BRASS}>
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[var(--co-ink-3)] transition-colors duration-[120ms] hover:bg-[var(--co-brass-wash)] hover:text-[var(--co-brass-ink)]"
                  title="Mark all as read"
                >
                  <CheckCheck className="h-4 w-4" />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-[var(--co-ink-3)] transition-colors duration-[120ms] hover:bg-[var(--co-burgundy-wash)] hover:text-[var(--co-burgundy)]"
                  title="Clear all"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="scrollbar-thin max-h-[420px] overflow-y-auto bg-[var(--co-page)]">
            {notifications.length === 0 ? (
              <div className="bg-[var(--co-raised)] px-6 py-12 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--co-line)] bg-[var(--co-selected)] text-[var(--co-ink-4)]">
                  <Bell className="h-4 w-4" />
                </div>
                <p className="text-[13px] font-semibold text-[var(--co-ink-2)]">No notifications yet</p>
                <p className="mt-1 text-[12px] text-[var(--co-ink-4)]">
                  We&apos;ll notify you when something happens
                </p>
              </div>
            ) : (
              [...notifications]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={() => markAsRead(notification.id)}
                  onRemove={() => handleRemoveNotification(notification.id)}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))
            )}
          </div>

          {/* Thin footer */}
          {notifications.length > 0 && (
            <div className="flex items-center justify-between gap-3 border-t border-[var(--co-line-subtle)] bg-[var(--co-raised)] px-4 py-[7px]">
              <span className="font-mono text-[9.5px] uppercase tracking-[.12em] text-[var(--co-ink-4)]">
                {`${notifications.length} ${notifications.length === 1 ? 'notification' : 'notifications'}`}
                {unreadCount > 0 && ` · ${unreadCount} unread`}
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  className="font-mono text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
